import { Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useState, useEffect } from 'react'
import {
  BarChart3,
  Facebook,
  MessageSquare,
  Settings,
  Home,
  TrendingUp,
  Calendar,
  Bell,
  Users,
  Activity,
  Camera,
  Film,
  Zap,
  Package
} from 'lucide-react'

const menuItems = [
  { 
    name: 'Dashboard', 
    icon: Home, 
    path: '/', 
    description: 'Übersicht & Statistiken' 
  },
  { 
    name: 'Pages', 
    icon: Facebook, 
    path: '/pages', 
    description: 'Facebook-Pages verwalten' 
  },
  { 
    name: 'Posts', 
    icon: MessageSquare, 
    path: '/posts', 
    description: 'Post-Performance analysieren' 
  },
  { 
    name: 'Stories', 
    icon: Camera, 
    path: '/stories', 
    description: 'Story-Performance analysieren' 
  },
  { 
    name: 'Reels', 
    icon: Film, 
    path: '/reels', 
    description: 'Facebook & Instagram Reels' 
  },
  {
    name: 'Viral Tracking',
    icon: Zap,
    path: '/viral',
    description: 'Echtzeit Reel & Story Tracking'
  },
  { 
    name: 'Analytics', 
    icon: BarChart3, 
    path: '/analytics', 
    description: 'Detaillierte Analysen' 
  },
  {
    name: 'Exporte',
    icon: Package,
    path: '/exporte',
    description: 'Vollexporte je Kanal'
  },
  { 
    name: 'User Management', 
    icon: Users, 
    path: '/user-management', 
    description: 'Benutzer & Rollen verwalten' 
  },
  {
    name: 'System Status',
    icon: Activity,
    path: '/cache-status',
    description: 'Scheduler & Services überwachen'
  },
  { 
    name: 'Einstellungen', 
    icon: Settings, 
    path: '/settings', 
    description: 'Konfiguration & Tokens' 
  },
]

export default function Sidebar() {
  const location = useLocation()
  
  // 🔐 Admin-Status aus localStorage prüfen
  const [isAdmin, setIsAdmin] = useState(false)
  
  useEffect(() => {
    const userData = localStorage.getItem('user')
    if (userData) {
      try {
        const user = JSON.parse(userData)
        setIsAdmin(user.role === 'admin')
      } catch (error) {
        console.error('Error parsing user data:', error)
        setIsAdmin(false)
      }
    }
  }, [])

  return (
    <div className="w-64 glass-card m-6 mr-0 p-6 flex flex-col">
      {/* Logo */}
      <div className="flex items-center space-x-3 mb-8">
        <div className="w-10 h-10 bg-gradient-to-r from-facebook-500 to-accent-500 rounded-lg flex items-center justify-center">
          <Facebook className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-white">Dashboard</h2>
          <p className="text-xs text-white/60">Creator Analytics</p>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-2">
        {menuItems.map((item, index) => {
          const Icon = item.icon
          const isActive = location.pathname === item.path
          
          // 🔐 Admin-Only Bereiche filtern
          if ((item.name === 'Exporte' || item.name === 'User Management' || item.name === 'System Status' || item.name === 'Einstellungen') && !isAdmin) {
            return null
          }
          
          return (
            <motion.div
              key={item.path}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Link
                to={item.path}
                className={`sidebar-item group ${isActive ? 'active' : ''}`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{item.name}</div>
                  <div className="text-xs text-white/50 group-hover:text-white/70 transition-colors">
                    {item.description}
                  </div>
                </div>
              </Link>
            </motion.div>
          )
        })}
      </nav>

      {/* Bottom Stats */}
      <div className="mt-8 space-y-4">
        {/* Quick Stats */}
        <div className="bg-white/5 rounded-lg p-4 border border-white/10">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-white/70">Letzte Sync</span>
            <Calendar className="w-4 h-4 text-white/50" />
          </div>
          <div className="text-xs text-white/90">
            Vor 2 Stunden
          </div>
        </div>

        <div className="bg-white/5 rounded-lg p-4 border border-white/10">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-white/70">Status</span>
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <TrendingUp className="w-4 h-4 text-green-400" />
            </div>
          </div>
          <div className="text-xs text-white/90">
            Alle Services online
          </div>
        </div>

        {/* Notifications */}
        <div className="flex items-center justify-center pt-4">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors"
          >
            <Bell className="w-5 h-5 text-white/70" />
          </motion.button>
        </div>
      </div>
    </div>
  )
} 